import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, getUploadUrl } from '../config/api';
import { useAuth } from '../context/AuthContext';

type Banner = { _id: string; image: string };
type Product = {
  _id: string;
  name: string;
  title?: string;
  image?: string;
  images?: string[];
  amountAndWeight?: string;
  price?: number;
  discountedPrice?: number;
  categoryId: string;
  categoryName?: string;
  createdAt?: string;
};

export function Catalog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const searchParam = searchParams.get('search') || searchParams.get('q') || '';
  const { isAuthenticated } = useAuth();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loadingBanners, setLoadingBanners] = useState(true);
  const [bannerIndex, setBannerIndex] = useState(0);
  const [bannerPaused, setBannerPaused] = useState(false);
  const [bannerTransition, setBannerTransition] = useState(true);
  const bannerResetRef = useRef(false);
  const bannerCount = banners.length;
  const duplicatedBanners =
    bannerCount === 1
      ? [banners[0], banners[0], banners[0]]
      : bannerCount === 2
        ? [...banners, ...banners, banners[0]]
        : [...banners, ...banners];
  const totalBannerSlides = duplicatedBanners.length;
  const [categories, setCategories] = useState<{ _id: string; name: string }[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>(categoryParam || '');
  const [searchQuery, setSearchQuery] = useState(searchParam);
  const [sortBy, setSortBy] = useState<'price-low' | 'price-high' | 'new'>('new');
  const [loading, setLoading] = useState(true);

  // Sync search from URL when navigating from Home (e.g. ?search=keyword)
  useEffect(() => {
    setSearchQuery(searchParam);
  }, [searchParam]);

  useEffect(() => {
    const loadBanners = async () => {
      try {
        const res = await axios.get(API.banners);
        if (res.data.success) setBanners(res.data.data || []);
      } catch {
        setBanners([]);
      } finally {
        setLoadingBanners(false);
      }
    };
    loadBanners();
  }, []);

  useEffect(() => {
    if (bannerCount > 0 && bannerIndex >= bannerCount) setBannerIndex(0);
  }, [bannerCount, bannerIndex]);

  useEffect(() => {
    if (bannerCount < 2 || bannerPaused) return;
    const id = setInterval(() => {
      setBannerIndex((i) => (i + 1) % bannerCount);
    }, 5000);
    return () => clearInterval(id);
  }, [bannerCount, bannerPaused]);

  const handleBannerTransitionEnd = () => {
    if (!bannerResetRef.current) return;
    bannerResetRef.current = false;
    setBannerTransition(false);
    setBannerIndex(0);
    requestAnimationFrame(() => requestAnimationFrame(() => setBannerTransition(true)));
  };

  const goToNext = () => {
    if (bannerCount <= 1) return;
    if (bannerIndex === bannerCount - 1) {
      bannerResetRef.current = true;
      setBannerTransition(true);
      setBannerIndex(bannerCount);
    } else {
      setBannerIndex((i) => i + 1);
    }
  };

  const goToPrev = () => {
    if (bannerCount <= 1) return;
    if (bannerIndex === 0) {
      setBannerTransition(false);
      setBannerIndex(bannerCount - 1);
      requestAnimationFrame(() => setBannerTransition(true));
    } else {
      setBannerIndex((i) => i - 1);
    }
  };

  const WEEKLY_DEALS_ID = 'weekly-deals';

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const cRes = await axios.get(API.categories);
        if (cRes.data.success) setCategories(cRes.data.data || []);
        const isWeeklyDeals = selectedCategory === WEEKLY_DEALS_ID;
        const url = selectedCategory && !isWeeklyDeals
          ? `${API.products}?categoryId=${encodeURIComponent(selectedCategory)}`
          : API.products;
        const pRes = await axios.get(url);
        if (pRes.data.success) {
          const list = pRes.data.data || [];
          setProducts(
            isWeeklyDeals
              ? list.filter(
                  (p: Product) =>
                    p.discountedPrice != null && Number(p.discountedPrice) > 0
                )
              : list
          );
        } else {
          setProducts([]);
        }
      } catch {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedCategory]);

  useEffect(() => {
    if (categoryParam) setSelectedCategory(categoryParam);
  }, [categoryParam]);

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    if (categoryId) {
      setSearchParams({ category: categoryId }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const q = searchQuery.trim().toLowerCase();
  const filteredProducts = q
    ? products.filter(
        (p) =>
          p.name?.toLowerCase().includes(q) ||
          (p.title && p.title.toLowerCase().includes(q)) ||
          (p.categoryName && p.categoryName.toLowerCase().includes(q))
      )
    : products;

  const getSortPrice = (p: Product): number => {
    if (p.discountedPrice != null && Number(p.discountedPrice) > 0) return Number(p.discountedPrice);
    if (p.price != null) return Number(p.price);
    return NaN;
  };

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === 'price-low') {
      const pa = getSortPrice(a);
      const pb = getSortPrice(b);
      const na = Number.isNaN(pa);
      const nb = Number.isNaN(pb);
      if (na && nb) return 0;
      if (na) return 1;
      if (nb) return -1;
      return pa - pb;
    }
    if (sortBy === 'price-high') {
      const pa = getSortPrice(a);
      const pb = getSortPrice(b);
      const na = Number.isNaN(pa);
      const nb = Number.isNaN(pb);
      if (na && nb) return 0;
      if (na) return 1;
      if (nb) return -1;
      return pb - pa;
    }
    if (sortBy === 'new') {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta;
    }
    return 0;
  });

  return (
    <div className="bg-slate-50/30">
      {/* ——— Welcome banner (above banner carousel) ——— */}
      <div className="w-full bg-orange-200 text-white py-12 text-center px-4">
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight">
          Welcome to AG Cash & Carry
        </h1>
        <p className="mt-3 sm:mt-4 text-xl sm:text-2xl md:text-3xl font-bold text-white/95 leading-snug">
          Your trusted catering partner bringing service, value and convenience!
        </p>
        <p className="mt-4 sm:mt-5 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
          Quality ingredients, sauces, packaging and drinks for takeaways, restaurants, coffee shops and catering businesses.
        </p>
      </div>
      {/* ——— Banner carousel (full width, 3 at once, same as Home) ——— */}
      <section className="relative overflow-hidden py-4 sm:py-6 w-full">
        <div className="w-full">
          {loadingBanners ? (
            <div className="w-full aspect-[21/9] max-h-64 sm:max-h-72 md:max-h-80 bg-slate-800/50 flex items-center justify-center text-slate-400 text-sm font-medium">Loading…</div>
          ) : banners.length === 0 ? (
            <div className="w-full aspect-[21/9] max-h-64 sm:max-h-72 md:max-h-80 bg-slate-800/50 flex items-center justify-center text-slate-400 text-sm font-medium">No banners yet</div>
          ) : (
            <div
              className="relative overflow-hidden w-full aspect-[21/9] max-h-64 sm:max-h-72 md:max-h-80"
              onMouseEnter={() => setBannerPaused(true)}
              onMouseLeave={() => setBannerPaused(false)}
            >
              <div
                className="flex h-full"
                style={{
                  width: `${(totalBannerSlides * 100) / 3}%`,
                  transform: `translateX(-${(bannerIndex * 100) / totalBannerSlides}%)`,
                  transition: bannerTransition ? 'transform 700ms cubic-bezier(0.22,1,0.36,1)' : 'none',
                }}
                onTransitionEnd={handleBannerTransitionEnd}
              >
                {duplicatedBanners.map((b, i) => (
                  <div
                    key={`${b._id}-${i}`}
                    className="flex-shrink-0 h-full px-1.5 sm:px-2 md:px-3"
                    style={{ width: `${100 / totalBannerSlides}%` }}
                  >
                    <div className="w-full h-full rounded-lg overflow-hidden">
                      <img
                        src={getUploadUrl(b.image)}
                        alt=""
                        className="w-full h-full object-cover object-center"
                        loading={i < 3 ? 'eager' : 'lazy'}
                        decoding="async"
                      />
                    </div>
                  </div>
                ))}
              </div>
              {bannerCount > 1 && (
                <>
                  <button
                    type="button"
                    onClick={goToPrev}
                    className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/95 shadow-xl flex items-center justify-center text-slate-800 hover:bg-white hover:scale-105 transition-all duration-200 z-10"
                    aria-label="Previous banner"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={goToNext}
                    className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/95 shadow-xl flex items-center justify-center text-slate-800 hover:bg-white hover:scale-105 transition-all duration-200 z-10"
                    aria-label="Next banner"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <div className="absolute bottom-2 sm:bottom-3 left-1/2 -translate-x-1/2 flex gap-2.5 z-10">
                    {banners.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setBannerIndex(i)}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          i === bannerIndex ? 'w-8 bg-amber-400' : 'w-1.5 bg-white/60 hover:bg-white/80'
                        }`}
                        aria-label={`Go to banner ${i + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </section>

      <div className="container mx-auto px-4 py-8 sm:py-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">
                Products
              </h1>
              <div className="mt-2 h-0.5 w-16 sm:w-20 bg-red-600 rounded-full" aria-hidden />
              <p className="mt-3 text-slate-600 text-sm sm:text-base max-w-2xl">
                Browse our range of wholesale food, frozen products, drinks, packaging and professional cleaning supplies.
              </p>
            </div>
            {!isAuthenticated && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 flex-shrink-0">
                Log in to see prices.
              </p>
            )}
          </div>

          {/* Category filter: All, Weekly Deals, then categories */}
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              type="button"
              onClick={() => handleCategoryChange('')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                !selectedCategory
                  ? 'bg-slate-800 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => handleCategoryChange(WEEKLY_DEALS_ID)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                selectedCategory === WEEKLY_DEALS_ID
                  ? 'bg-slate-800 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Weekly Deals
            </button>
            {categories.map((c) => (
              <button
                key={c._id}
                type="button"
                onClick={() => handleCategoryChange(c._id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  selectedCategory === c._id
                    ? 'bg-slate-800 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          {/* Search products */}
          <div className="mb-8">
            <label htmlFor="product-search" className="sr-only">
              Search products
            </label>
            <div className="relative max-w-xl">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
              <input
                id="product-search"
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products by name, title or category…"
                className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-12 pr-4 text-slate-800 placeholder-slate-400 focus:ring-2 transition"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  aria-label="Clear search"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="mt-2 text-sm text-slate-500">
                {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} match your search
              </p>
            )}
          </div>

          {/* Sort by */}
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <label htmlFor="sort-by" className="text-sm font-medium text-slate-700">
              Sort by:
            </label>
            <select
              id="sort-by"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'price-low' | 'price-high' | 'new')}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
            >
              <option value="price-low">Price: low to high</option>
              <option value="price-high">Price: high to low</option>
              <option value="new">New arrivals</option>
            </select>
          </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="rounded-xl bg-slate-100 h-72 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {sortedProducts.map((p) => (
              <Link
                key={p._id}
                to={`/products/${p._id}`}
                className="rounded-xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg hover:border-slate-300 transition block"
              >
                <div className="aspect-square w-full bg-slate-100 overflow-hidden">
                  {(p.images?.length ? p.images[0] : p.image) ? (
                    <img
                      src={getUploadUrl(p.images?.length ? p.images[0] : p.image)}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full" aria-hidden />
                  )}
                </div>
                <div className="p-3 sm:p-4">
                  <p className="font-medium text-slate-800">{p.name}</p>
                  {p.amountAndWeight ? (
                    <p className="text-sm text-slate-500 mt-0.5">{p.amountAndWeight}</p>
                  ) : null}
                  {isAuthenticated && (
                    p.discountedPrice != null && Number(p.discountedPrice) > 0 ? (
                      <div className="mt-2 flex flex-wrap items-baseline gap-2">
                        <p className="text-amber-600 font-semibold">£{Number(p.discountedPrice).toFixed(2)}</p>
                        {p.price != null && (
                          <p className="text-slate-400 text-sm line-through">£{Number(p.price).toFixed(2)}</p>
                        )}
                      </div>
                    ) : p.price != null ? (
                      <p className="mt-2 text-amber-600 font-semibold">£{Number(p.price).toFixed(2)}</p>
                    ) : null
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}

        {!loading && products.length === 0 && (
          <p className="text-slate-500 py-12 text-center">No products in this category.</p>
        )}
        {!loading && products.length > 0 && filteredProducts.length === 0 && (
          <p className="text-slate-500 py-12 text-center">No products match your search. Try a different term.</p>
        )}
        </div>
      </div>
    </div>
  );
}
